/**
 * TanStack Query hooks платформенной очереди разбора культур.
 *
 * Все мутации инвалидируют ключ `['crop-import-queue']` целиком: разбор одной
 * строки меняет и сводку, и (через alias) резолвабельность соседних терминов —
 * точечный patch кэша тут врал бы.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type {
  CreateCropPayload,
  CropQueueItem,
  CropQueueList,
  CropQueueQuery,
  MapPayload,
  RematchResult,
} from './types';

const BASE = '/api/v1/reference/crop-import-queue';

export const cropQueueKeys = {
  all: ['crop-import-queue'] as const,
  list: (q: CropQueueQuery) => ['crop-import-queue', 'list', q] as const,
};

function buildParams(q: Record<string, unknown>): string {
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(q)) {
    if (v === undefined || v === null || v === '') continue;
    params.set(k, String(v));
  }
  const s = params.toString();
  return s ? `?${s}` : '';
}

export function useCropQueue(query: CropQueueQuery) {
  return useQuery({
    queryKey: cropQueueKeys.list(query),
    queryFn: () => api.get<CropQueueList>(`${BASE}${buildParams({ ...query })}`),
    staleTime: 15_000,
  });
}

function useInvalidate() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: cropQueueKeys.all });
}

/** «Перепроверить» — закрывает термины, для которых канон/алиас уже появился. */
export function useRematchQueue() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: () => api.post<RematchResult>(`${BASE}/rematch`, {}),
    onSuccess: invalidate,
  });
}

export function useMapQueueItem() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: MapPayload }) =>
      api.post<CropQueueItem>(`${BASE}/${id}/map`, payload),
    onSuccess: invalidate,
  });
}

export function useCreateCropFromQueue() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: CreateCropPayload }) =>
      api.post<CropQueueItem>(`${BASE}/${id}/create-crop`, payload),
    onSuccess: invalidate,
  });
}

export function useDeleteQueueItem() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`${BASE}/${id}`),
    onSuccess: invalidate,
  });
}
